import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

// Tooltip position variants - only custom ones that need special handling
export type TooltipPosition = "center-left" | "center-right";

// Tooltip arrow variants
export type TooltipArrowVariant = "default" | "none";

// Extended TooltipContent props
export interface TooltipContentProps
  extends React.ComponentProps<typeof TooltipPrimitive.Content> {
  className?: string;
  sideOffset?: number;
  children: React.ReactNode;
  position?: TooltipPosition;
  arrowVariant?: TooltipArrowVariant;
}

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  position,
  arrowVariant = "default",
  ...props
}: TooltipContentProps) {
  // Handle custom center positions with special logic
  let side: "top" | "right" | "bottom" | "left" | undefined;
  let align: "center" | "start" | "end" | undefined;
  let customOffset = sideOffset;

  if (position === "center-left") {
    side = "left";
    align = "center";
    // For center-left, we want the tooltip to appear to the left but centered vertically
    customOffset = 8; // Small offset to bring it closer
  } else if (position === "center-right") {
    side = "right";
    align = "center";
    // For center-right, we want the tooltip to appear to the right but centered vertically
    customOffset = 8; // Small offset to bring it closer
  }

  // Add position-specific classes only for custom positions
  // Note: We don't add wl-tooltip-center-left/right classes as they conflict with Radix UI positioning
  const arrowClass = arrowVariant === "none" ? "wl-tooltip-arrow-none" : "";

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        side={side}
        align={align}
        sideOffset={customOffset}
        className={`wl-tooltip-content ${arrowClass} ${className || ""}`}
        {...props}
      >
        {children}
        {arrowVariant !== "none" && (
          <TooltipPrimitive.Arrow className="wl-tooltip-arrow" />
        )}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
